import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import SEOHead from '@/components/base/SEOHead';
import { movies, categories } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const [activeSubcategory, setActiveSubcategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const categoryInfo = categories.find((c) => c.slug === slug);

  const categoryName = categoryInfo ? categoryInfo.name : '';
  const canonicalUrl = `https://mogutv.com/category/${slug}`;

  const categoryMovies = useMemo(() => {
    let filtered = movies.filter((m) => m.category === slug);

    if (activeSubcategory !== 'all') {
      filtered = filtered.filter((m) => m.subcategory === activeSubcategory);
    }

    if (sortBy === 'newest') {
      filtered = [...filtered].sort((a, b) => b.year - a.year);
    } else if (sortBy === 'hottest') {
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'rating') {
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
    }

    return filtered;
  }, [slug, activeSubcategory, sortBy]);

  const subcategories = useMemo(() => {
    const subs = new Set<string>();
    movies
      .filter((m) => m.category === slug)
      .forEach((m) => subs.add(m.subcategory));
    return Array.from(subs);
  }, [slug]);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '蘑菇影视首页',
        item: 'https://mogutv.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: canonicalUrl,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `蘑菇影视${categoryName} | ${categoryName}高清免费在线观看`,
    description: `蘑菇影视为您提供最新最热门的${categoryName}内容，涵盖各类${categoryName}高清免费在线观看，播放流畅不缓冲。每日更新，精彩不断。`,
    url: canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: '蘑菇影视',
      url: 'https://mogutv.com',
    },
  };

  if (!categoryInfo) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <p className="text-foreground-500">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`蘑菇影视${categoryName} | ${categoryName}高清免费在线观看`}
        description={`蘑菇影视为您提供最新最热门的${categoryName}内容，涵盖各类${categoryName}高清免费在线观看，播放流畅不缓冲。每日更新，精彩不断。`}
        canonicalUrl={canonicalUrl}
        ogType="website"
        structuredData={{
          '@graph': [breadcrumbSchema, webPageSchema],
        }}
      />
      <Navbar />

      <main className="min-h-screen bg-background-50">
        {/* Breadcrumb */}
        <div className="pt-20 md:pt-24 pb-4 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200">
                {t('common.breadcrumbHome')}
              </Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium">
                {categoryInfo.name}
              </span>
            </nav>
          </div>
        </div>

        {/* Page Header */}
        <div className="py-6 md:py-8 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {categoryInfo.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              发现最新最热门的{categoryInfo.name}内容，高清流畅在线观看
            </p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="sticky top-16 z-30 bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-3">
              {/* Subcategory Filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setActiveSubcategory('all')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSubcategory === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {t('category.all')}
                </button>
                {subcategories.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setActiveSubcategory(sub)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSubcategory === sub
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>

              {/* Sort */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  {t('category.sortBy')}
                </span>
                <div className="flex items-center bg-background-100 rounded-full p-0.5">
                  {[
                    { key: 'newest', label: t('category.newest') },
                    { key: 'hottest', label: t('category.hottest') },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === opt.key
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Movie Grid */}
        <section className="py-8 md:py-10">
          <div className="w-full px-4 md:px-6">
            {categoryMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {categoryMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-movie-2-line text-4xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}